import { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, LogOut, ShieldCheck } from "lucide-react";

const APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const LOC_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const PLANS = [
  { id: "monthly", label: "Monthly", priceKey: "monthly_price", suffix: "/month", note: "Billed monthly" },
  { id: "annual", label: "Annual", priceKey: "annual_price", suffix: "/year", note: "Save one month — best value", badge: "Save 1 month" },
];

export default function Subscribe({ onDone }) {
  const { user, logout, setUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const fmt = (amount) => {
    const cur = config?.currency || "USD";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: 0 }).format((amount || 0) / 100);
  };

  const onToken = async (token) => {
    if (!token?.token) { toast.error("Your card could not be processed. Please try again."); return; }
    setBusy(true);
    try {
      await api.post("/billing/subscribe", {
        source_id: token.token, plan,
        postal_code: token.details?.billing?.postalCode || "",
      });
      try { const me = await api.get("/auth/me"); setUser(me.data); } catch (e) {}
      toast.success(compExpired ? "Welcome back! Your membership is active." : `You're in! Your ${config?.trial_days || 7}-day free trial has started.`);
      if (onDone) onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const trialDays = config?.trial_days || 7;
  const configured = APP_ID && LOC_ID;
  const CURRENCY_COUNTRY = { CAD: "CA", USD: "US", GBP: "GB", AUD: "AU", EUR: "IE", JPY: "JP" };
  const countryCode = CURRENCY_COUNTRY[config?.currency] || "US";
  const compExpired = !!status?.comp_expired;

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col" data-testid="subscribe-page">
      <header className="flex items-center justify-between px-8 py-5 border-b border-[#EBE5E0] bg-white">
        <span className="font-serif text-2xl text-[#A68A6B]">WorkRoom</span>
        <button data-testid="subscribe-logout" onClick={logout}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B]">
          <LogOut size={15} /> Sign out
        </button>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-10">
          {/* Left: pitch + plans */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#A68A6B] mb-3">Membership</p>
            {compExpired ? (
              <>
                <h1 data-testid="comp-expired-heading" className="font-serif text-4xl text-[#2B2622] leading-tight">Your free trial period has ended</h1>
                <p className="text-sm text-[#5C5651] mt-3 max-w-md">
                  Please subscribe for continued access and organized admin bliss. Choose a plan below and add a card to pick up right where you left off — cancel anytime.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-serif text-4xl text-[#2B2622] leading-tight">Start your {trialDays}-day free trial</h1>
                <p className="text-sm text-[#5C5651] mt-3 max-w-md">
                  Hi {user?.name?.split(" ")[0] || "there"} — unlock every WorkRoom tool for your studio. Add a card to begin;
                  you won't be charged until your trial ends, and you can cancel anytime.
                </p>
              </>
            )}

            <div className="mt-8 space-y-3">
              {PLANS.map((p) => {
                const selected = plan === p.id;
                return (
                  <button key={p.id} type="button" data-testid={`plan-${p.id}`} onClick={() => setPlan(p.id)}
                    className={`w-full text-left border p-5 transition-colors flex items-center justify-between ${selected ? "border-[#A68A6B] bg-white ring-1 ring-[#A68A6B]" : "border-[#EBE5E0] bg-white hover:border-[#C9BBAE]"}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-serif text-xl text-[#2B2622]">{p.label}</span>
                        {p.badge && <span className="text-[10px] uppercase tracking-wider bg-[#A68A6B] text-white px-2 py-0.5">{p.badge}</span>}
                      </div>
                      <p className="text-xs text-[#9b938b] mt-1">{p.note}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-2xl text-[#8C7154]">{config ? fmt(config[p.priceKey]) : "—"}</div>
                      <div className="text-[11px] text-[#9b938b]">{p.suffix}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <ul className="mt-8 space-y-2">
              {["All projects, budgets & approvals", "Mood boards, FF&E & Product Library", "Branded PDF exports & client portal"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#5C5651]"><Check size={15} className="text-[#A68A6B]" /> {f}</li>
              ))}
            </ul>
          </div>

          {/* Right: payment */}
          <div className="bg-white border border-[#EBE5E0] p-7">
            <h2 className="font-serif text-2xl text-[#2B2622]">Payment details</h2>
            <p className="text-xs text-[#9b938b] mt-1 mb-5 flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-[#A68A6B]" /> Securely processed by Square. We never see your card number.
            </p>

            {!configured ? (
              <div className="border border-dashed border-[#D9CBBF] p-6 text-sm text-[#9E4747]">
                Payments aren't configured yet. Please contact support.
              </div>
            ) : (
              <div data-testid="square-payment-form">
                <PaymentForm
                  applicationId={APP_ID}
                  locationId={LOC_ID}
                  cardTokenizeResponseReceived={onToken}
                  createPaymentRequest={() => ({
                    countryCode,
                    currencyCode: config?.currency || "USD",
                    total: { amount: "0", label: "Free trial" },
                  })}
                >
                  <CreditCard
                    buttonProps={{ isLoading: busy }}
                    render={(Button) => (
                      <Button css={{ backgroundColor: "#A68A6B", "&:hover": { backgroundColor: "#8C7154" }, borderRadius: "0px" }}>
                        {busy ? "Processing…" : (compExpired ? "Subscribe & continue" : `Start ${trialDays}-day free trial`)}
                      </Button>
                    )}
                  />
                </PaymentForm>
                <p className="text-[11px] text-[#9b938b] mt-4 leading-relaxed">
                  After your {trialDays}-day trial, you'll be charged {config ? fmt(config[plan === "annual" ? "annual_price" : "monthly_price"]) : ""}{plan === "annual" ? "/year" : "/month"} unless you cancel. Cancel anytime from Settings.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
